import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { usePermissions } from '../context/PermissionsContext';
import FileUploader from './FileUploader';

export default function ProjectNotesPanel({ project, onClose }) {
  const { role }                      = usePermissions();
  const canDelete                     = ['gpf_administrator', 'gpf_system_admin', 'administrator'].includes(role);

  const [notes,        setNotes]        = useState([]);
  const [noteText,     setNoteText]     = useState('');
  const [hours,        setHours]        = useState('');
  const [saving,       setSaving]       = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [aiLoading,    setAiLoading]    = useState(false);
  const [aiResponse,   setAiResponse]   = useState('');
  const [aiInput,      setAiInput]      = useState('');
  const [showAi,       setShowAi]       = useState(false);
  const [fileIds,      setFileIds]      = useState([]);
  const notesEndRef                     = useRef(null);

  useEffect(() => {
    fetchNotes();
    fetchFileIds();
  }, [project.id]);

  const fetchFileIds = async () => {
    try {
      const res = await api.get(`/wp/v2/projects/${project.id}`);
      const ids = JSON.parse(res.data.meta?.gpf_file_ids || '[]');
      setFileIds(ids);
    } catch(e) { console.error(e); }
  };

  useEffect(() => {
    notesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [notes]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/wp/v2/notes?project_id=${project.id}&per_page=100&orderby=date&order=asc`);
      setNotes(res.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await api.post('/wp/v2/notes', {
        title:   noteText.slice(0, 60),
        content: noteText,
        status:  'publish',
        meta: {
          gpf_project_id:  project.id,
          gpf_task_id:     0,
          gpf_hours_logged: parseFloat(hours) || 0,
        }
      });
      setNoteText('');
      setHours('');
      fetchNotes();
    } catch(e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDeleteNote = async (noteId) => {
    if (!canDelete) return;
    if (!window.confirm('Delete this note?')) return;
    try {
      await api.delete(`/wp/v2/notes/${noteId}?force=true`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch(e) { console.error(e); }
  };

  const handleAnalyze = async () => {
    setAiLoading(true);
    setAiResponse('');
    setShowAi(true);
    try {
      const res = await api.post('/gpf/v1/ai-agent/analyze', {
        project_id:   project.id,
        project_name: project.title?.rendered,
        description:  project.content?.rendered || '',
        context:      'project',
      });
      setAiResponse(res.data?.analysis || 'Analysis complete.');
    } catch(e) {
      setAiResponse('AI analysis unavailable.');
    } finally { setAiLoading(false); }
  };

  const handleAiChat = async () => {
    if (!aiInput.trim()) return;
    setAiLoading(true);
    const question = aiInput;
    setAiInput('');
    try {
      const res = await api.post('/gpf/v1/ai-agent/chat', {
        project_id: project.id,
        message:    question,
        context:    'project',
      });
      setAiResponse(res.data?.reply || '');
    } catch(e) {
      setAiResponse('Unable to get a response.');
    } finally { setAiLoading(false); }
  };

  return (
    <div style={styles.panel}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.headerLabel}>Project Notes</div>
          <div style={styles.headerTitle}>{project.title?.rendered || 'Project'}</div>
        </div>
        <button onClick={onClose} style={styles.closeBtn}>✕</button>
      </div>

      {/* Notes list */}
      <div style={styles.notesList}>
        {loading && <p style={styles.empty}>Loading...</p>}
        {!loading && notes.length === 0 && (
          <p style={styles.empty}>No notes yet. Add the first one below.</p>
        )}
        {notes.map(note => (
          <div key={note.id} style={styles.noteItem}>
            <div style={styles.noteBody} dangerouslySetInnerHTML={{ __html: note.content?.rendered || '' }} />
            <div style={styles.noteMeta}>
              <span>{note.meta?.gpf_created_by_name || 'Team'}</span>
              <span>·</span>
              <span>{note.date ? new Date(note.date).toLocaleDateString() : ''}</span>
              {note.meta?.gpf_hours_logged > 0 && (
                <span style={styles.hoursTag}>⏱ {note.meta.gpf_hours_logged}h</span>
              )}
              {canDelete && (
                <button onClick={() => handleDeleteNote(note.id)} style={styles.deleteBtn}>🗑</button>
              )}
            </div>
          </div>
        ))}
        <div ref={notesEndRef} />
      </div>

      {/* Add Note */}
      <div style={styles.addSection}>
        <div style={styles.addLabel}>Add Note</div>
        <textarea
          style={styles.textarea}
          placeholder="Write a note for the project team..."
          value={noteText}
          onChange={e => setNoteText(e.target.value)}
          rows={3}
        />
        <div style={styles.noteFooter}>
          <input
            style={styles.hoursInput}
            type="number"
            min="0"
            step="0.25"
            placeholder="Hours"
            value={hours}
            onChange={e => setHours(e.target.value)}
          />
          <span style={{ fontSize: 12, color: '#94a3b8' }}>hours logged</span>
          <button
            style={{ ...styles.saveBtn, opacity: saving || !noteText.trim() ? 0.6 : 1 }}
            onClick={handleSaveNote}
            disabled={saving || !noteText.trim()}
          >
            {saving ? 'Saving...' : 'Save Note'}
          </button>
        </div>

        {/* File Uploader */}
        <div style={{ marginTop: 12 }}>
          <FileUploader projectId={project.id} existingIds={fileIds} onUpdate={() => { fetchNotes(); fetchFileIds(); }} />
        </div>

        {/* AI Agent */}
        <div style={styles.aiSection}>
          <div style={styles.aiHeader}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>🤖 AI Agent</span>
            <button style={styles.analyzeBtn} onClick={handleAnalyze} disabled={aiLoading}>
              {aiLoading && !aiInput ? 'Analyzing...' : 'Analyze Project'}
            </button>
          </div>

          {showAi && (
            <div style={styles.aiBox}>
              {aiLoading && !aiResponse && <p style={{ color: '#94a3b8', fontSize: 13 }}>Analyzing project...</p>}
              {aiResponse && <p style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.6, margin: 0 }}>{aiResponse}</p>}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <input
                  style={styles.aiInput}
                  placeholder="Ask about this project..."
                  value={aiInput}
                  onChange={e => setAiInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAiChat()}
                />
                <button style={styles.aiSendBtn} onClick={handleAiChat} disabled={aiLoading || !aiInput.trim()}>
                  {aiLoading && aiInput ? '...' : '→'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  panel:       { position: 'fixed', right: 0, top: 0, bottom: 0, width: 380, background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 200, display: 'flex', flexDirection: 'column' },
  header:      { background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', padding: '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 },
  headerLabel: { fontSize: 10, color: 'rgba(255,255,255,0.7)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  headerTitle: { fontSize: 15, fontWeight: 700, color: 'white', lineHeight: 1.3, paddingRight: 32 },
  closeBtn:    { background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 14, flexShrink: 0 },
  notesList:   { flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 },
  empty:       { color: '#94a3b8', fontSize: 13, textAlign: 'center', marginTop: 32 },
  noteItem:    { background: '#f8fafc', borderRadius: 8, padding: '10px 14px', borderLeft: '3px solid #2563eb' },
  noteBody:    { fontSize: 13, color: '#1e293b', lineHeight: 1.6, margin: '0 0 6px' },
  noteMeta:    { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#94a3b8', flexWrap: 'wrap' },
  hoursTag:    { background: '#eff6ff', color: '#2563eb', padding: '1px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  deleteBtn:   { background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0, marginLeft: 'auto' },
  addSection:  { padding: 16, borderTop: '1px solid #e2e8f0', flexShrink: 0 },
  addLabel:    { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 },
  textarea:    { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  noteFooter:  { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 },
  hoursInput:  { width: 70, padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none' },
  saveBtn:     { marginLeft: 'auto', padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  aiSection:   { marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 12 },
  aiHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  analyzeBtn:  { padding: '6px 12px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  aiBox:       { background: '#f5f3ff', borderRadius: 8, padding: 12, border: '1px solid #ddd6fe' },
  aiInput:     { flex: 1, padding: '6px 10px', border: '1.5px solid #ddd6fe', borderRadius: 6, fontSize: 13, outline: 'none' },
  aiSendBtn:   { padding: '6px 12px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 },
};